import React from "react";

export type ElementType<T> = { value: T; label: string };

export type AutocompleteHookType<T> = {
    open: boolean;
    searchValue: string;
    items: Array<ElementType<T>>;
    itemSelectedLabel: string;
    onItemSelected: (element: ElementType<T>) => void;
    onAutocompleteFocus: () => void;
    onClearClicked: () => void;
    onSearchValueChange: (value: any) => void;
    onResetClick: () => void;
    closeSelection: () => void;
    inputRef: any;
    loading: boolean;
};

export type SetItemInterface<T> = React.Dispatch<React.SetStateAction<Array<ElementType<T>>>>;

export type GetItemsInterface<T> = (
    setItems: SetItemInterface<T>,
    value: string
) => AbortController;

export type OnChangeInterface<T> = (element: ElementType<T> | undefined) => void;

function useAutocomplete<T>(
    getItems: GetItemsInterface<T>,
    onChange: OnChangeInterface<T>,
    itemSelectedLabel: string,
    searchFromZero?: boolean,
    onAutocompleteCheckboxChange?: (element: ElementType<T> | undefined, checked: boolean) => void
): AutocompleteHookType<T> {
    const [open, setOpen] = React.useState<boolean>(false);
    const [searchValue, setSearchValue] = React.useState<string>("");
    const [items, setItems] = React.useState<Array<ElementType<T>>>([]);
    const inputRef = React.useRef<HTMLInputElement>(null);

    const showLoading = React.useRef<boolean>(true);
    const [loading, setLoading] = React.useState<boolean>(false);

    const prevAbortController = React.useRef<AbortController | null>(null);

    const onItemSelected = React.useCallback(
        (element: ElementType<T>) => {
            onChange(element);

            setOpen(false);
        },
        [onChange]
    );

    const onClearClicked = React.useCallback(() => {
        setSearchValue("");

        onChange(undefined);

        if (onAutocompleteCheckboxChange) {
            onAutocompleteCheckboxChange(undefined, false);
        }

        setOpen(false);

        setItems([]);

        if (!showLoading.current) {
            showLoading.current = true;
        }
    }, [onChange, onAutocompleteCheckboxChange]);

    const onSearchValueChange = React.useCallback(
        (value: string) => {
            setSearchValue(value);

            if (searchFromZero || value.length > 2) {
                if (showLoading.current) {
                    setLoading(true);

                    showLoading.current = false;
                } else {
                    setLoading((prevState) => {
                        if (prevState) {
                            return !prevState;
                        }

                        return prevState;
                    });
                }

                if (prevAbortController.current) {
                    prevAbortController.current.abort();

                    prevAbortController.current = null;
                }

                prevAbortController.current = getItems(setItems, value);
            } else {
                setItems([]);
            }
        },
        [getItems, searchFromZero]
    );

    const onAutocompleteFocus = React.useCallback(() => {
        setOpen(true);

        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }

            if (searchFromZero) {
                onSearchValueChange("");
            }
        }, 5);
    }, [onSearchValueChange, searchFromZero]);

    const onResetClick = React.useCallback(() => {
        setSearchValue("");

        setOpen(false);

        setItems([]);
    }, []);

    const closeSelection = React.useCallback(() => {
        setOpen(false);
    }, []);

    return React.useMemo(() => {
        return {
            open,
            searchValue,
            items,
            itemSelectedLabel,
            onItemSelected,
            onAutocompleteFocus,
            onClearClicked,
            onSearchValueChange,
            onResetClick,
            closeSelection,
            inputRef,
            loading,
        };
    }, [
        open,
        searchValue,
        items,
        itemSelectedLabel,
        onItemSelected,
        onAutocompleteFocus,
        onClearClicked,
        onSearchValueChange,
        onResetClick,
        closeSelection,
        inputRef,
        loading,
    ]);
}

export default useAutocomplete;
